import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Identity } from "@dfinity/agent";
import {
  restoreIdentity,
  signIn,
  signInAnonymously,
  signOut,
} from "../lib/auth";

interface AuthValue {
  identity: Identity | null;
  loading: boolean;
  error: string;
  login: () => Promise<void>;
  loginAnonymously: () => void;
  logout: () => Promise<void>;
  retry: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const retry = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setIdentity(await restoreIdentity());
    } catch {
      setError("Could not check your session. Check your connection and retry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void retry();
  }, [retry]);

  const login = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setIdentity(await signIn());
    } catch {
      setError("Sign-in was not completed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loginAnonymously = useCallback(() => {
    setError("");
    try {
      setIdentity(signInAnonymously());
    } catch {
      setError("Could not start the local anonymous session.");
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut();
    setIdentity(null);
  }, []);

  const value = useMemo(
    () => ({ identity, loading, error, login, loginAnonymously, logout, retry }),
    [identity, loading, error, login, loginAnonymously, logout, retry],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
